import { useCallback, useEffect, useRef, useState } from 'react';
import { scenariosDict } from '../support/dictionaries';
import { createPeer, DIRECT_CONNECTION_TIMEOUT_MS, gatherDescription, pairingRequest, readMessage, sendMessage } from './connection';
import { createHttpChannel } from './httpChannel';
import { remoteErrorKey, withRemoteLanguage } from './remoteI18n';

const videoKeys = new Map(Object.entries(scenariosDict).map(([key, url]) => [url, key]));
const HTTP_OFFER = { type: 'offer', sdp: 'v=0\r\na=x-teddy-http-only\r\n' };
const viewerLabels = {
    ready: 'viewerReady', playing: 'viewerPlaying',
    paused: 'viewerPaused', waiting: 'viewerWaiting',
    blocked: 'viewerBlocked',
    error: 'viewerVideoError',
};

export default function useRemoteController(playerRef, curVideo, preloadedVideo, enabled, lang = 'en') {
    const active = useRef(null);
    const source = useRef({ curVideo, preloadedVideo });
    source.current = { curVideo, preloadedVideo };
    const [connection, setConnection] = useState({ status: 'idle', link: '', messageKey: '' });

    const dispose = useCallback(() => {
        const session = active.current;
        active.current = null;
        if (!session) return;
        session.abort.abort();
        clearTimeout(session.poll);
        clearTimeout(session.timeout);
        clearTimeout(session.fallback);
        session.channel?.close();
        session.peer?.close();
        if (session.room) {
            pairingRequest(`/${session.room.id}`, {
                method: 'DELETE', token: session.room.hostToken,
            }).catch(() => {});
        }
    }, []);
    useEffect(() => dispose, [dispose]);

    const publish = useCallback(event => {
        const session = active.current;
        if (session?.channel?.readyState !== 'open') return;
        const video = playerRef.current?.getInternalPlayer?.();
        const { curVideo: url, preloadedVideo: next } = source.current;
        if (!(video instanceof HTMLVideoElement) || video.readyState < 1) return;
        if (video.currentSrc !== new URL(url, window.location.href).href) return;
        session.revision += 1;
        sendMessage(session.channel, {
            type: 'playback', video: videoKeys.get(url),
            time: video.currentTime, paused: video.paused || video.ended,
            rate: video.playbackRate, next: videoKeys.get(next) || null,
            seek: event?.type === 'seeked', revision: session.revision,
        });
    }, [playerRef]);

    useEffect(() => {
        if (!enabled) return undefined;
        let attached;
        const events = ['play', 'pause', 'seeked', 'ended', 'loadedmetadata', 'ratechange'];
        const tick = () => {
            const video = playerRef.current?.getInternalPlayer?.();
            if (video !== attached) {
                events.forEach(event => attached?.removeEventListener(event, publish));
                attached = video;
                events.forEach(event => attached?.addEventListener(event, publish));
            }
            publish();
        };
        const timer = setInterval(tick, 500);
        tick();
        return () => {
            clearInterval(timer);
            events.forEach(event => attached?.removeEventListener(event, publish));
        };
    }, [enabled, playerRef, publish]);

    const disconnect = useCallback(() => {
        dispose();
        setConnection({ status: 'idle', link: '', messageKey: '' });
    }, [dispose]);
    useEffect(() => {
        if (!enabled) {
            dispose();
            setConnection(current => current.status === 'idle' ? current : { status: 'idle', link: '', messageKey: '' });
        }
    }, [enabled, dispose]);

    const connect = async () => {
        dispose();
        setConnection({ status: 'preparing', link: '', messageKey: '' });
        const session = { abort: new AbortController(), revision: 0 };
        active.current = session;
        const fail = messageKey => {
            if (active.current !== session) return;
            dispose();
            setConnection({ status: 'error', link: '', messageKey });
        };
        const attach = (channel, transport) => {
            session.channel = channel;
            const current = () => active.current === session && session.channel === channel;
            const confirmConnection = () => {
                if (!current()) return;
                session.transport = transport;
                clearTimeout(session.timeout);
                clearTimeout(session.poll);
                clearTimeout(session.fallback);
                setConnection({ status: 'connected', transport, link: '', messageKey: viewerLabels.waiting });
                publish();
                if (transport === 'direct') {
                    pairingRequest(`/${session.room.id}`, {
                        method: 'DELETE', token: session.room.hostToken,
                    }).catch(() => {});
                }
            };
            channel.onopen = () => {
                if (!current()) return;
                // A local open event can precede the viewer's event. Keep the
                // relay available until the viewer confirms its direct channel.
                if (transport === 'direct') publish();
                else confirmConnection();
            };
            channel.onmessage = event => {
                if (!current()) return;
                const message = readMessage(event);
                if (message?.type === 'ready') {
                    if (transport === 'direct' && !session.transport) confirmConnection();
                    else publish();
                }
                if (message?.type === 'status' && viewerLabels[message.state]) {
                    setConnection(value => ({ ...value, messageKey: viewerLabels[message.state] }));
                }
            };
            channel.onclose = () => {
                if (!current()) return;
                if (!session.transport) startHttp();
                else fail('controllerDisconnected');
            };
        };
        const startHttp = () => {
            if (active.current !== session || session.transport) return;
            if (!session.room) {
                session.directUnavailable = true;
                return;
            }
            session.transport = 'http';
            clearTimeout(session.poll);
            clearTimeout(session.fallback);
            const previous = session.channel;
            attach(createHttpChannel({ room: session.room, token: session.room.hostToken, role: 'host' }), 'http');
            previous?.close();
            session.peer?.close();
            setConnection(value => ({ ...value, status: 'connecting', messageKey: 'connectingServer' }));
        };
        try {
            let offer = HTTP_OFFER;
            try {
                session.peer = createPeer();
                attach(session.peer.createDataChannel('teddy-playback', { ordered: true }), 'direct');
                await session.peer.setLocalDescription(await session.peer.createOffer());
                offer = await gatherDescription(session.peer, session.abort.signal);
            } catch (error) {
                if (session.abort.signal.aborted) return;
                session.channel = null;
                session.peer?.close();
                session.peer = null;
            }
            session.room = await pairingRequest('', {
                method: 'POST', body: JSON.stringify(offer), signal: session.abort.signal,
            });
            if (active.current !== session) return;
            const link = new URL('/remote-control/view', window.location.origin);
            link.hash = new URLSearchParams({ room: session.room.id, token: session.room.viewerToken }).toString();
            setConnection({ status: 'waiting', link: link.href, messageKey: '' });
            session.timeout = setTimeout(() => fail('linkExpired'), session.room.expiresIn * 1000);
            if (!session.peer || session.directUnavailable) {
                startHttp();
                return;
            }
            session.peer.onconnectionstatechange = () => {
                if (active.current !== session || session.transport === 'http') return;
                if (['disconnected', 'failed', 'closed'].includes(session.peer.connectionState)) {
                    if (!session.transport) startHttp();
                    else fail('controllerDisconnected');
                }
            };
            const poll = async () => {
                try {
                    const [answer, status] = await Promise.all([
                        pairingRequest(`/${session.room.id}/answer`, { token: session.room.hostToken, signal: session.abort.signal }),
                        pairingRequest(`/${session.room.id}/status`, { token: session.room.hostToken, signal: session.abort.signal }),
                    ]);
                    if (active.current !== session || session.transport) return;
                    if (status?.connected && status.transport === 'http') {
                        startHttp();
                    } else if (answer) {
                        setConnection(value => ({ ...value, status: 'connecting' }));
                        session.fallback = setTimeout(startHttp, DIRECT_CONNECTION_TIMEOUT_MS);
                        try {
                            await session.peer.setRemoteDescription(answer);
                        } catch {
                            startHttp();
                        }
                    } else {
                        session.poll = setTimeout(poll, 1000);
                    }
                } catch (error) {
                    if (error.name !== 'AbortError' && active.current === session && !session.transport) fail(remoteErrorKey(error));
                }
            };
            poll();
        } catch (error) {
            if (error.name !== 'AbortError') fail(remoteErrorKey(error));
        }
    };
    return { ...connection, link: withRemoteLanguage(connection.link, lang), connect, disconnect };
}
